import { inject, Injectable } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { ROUTER_NAVIGATED } from '@ngrx/router-store';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { exhaustMap, fromEvent, of, withLatestFrom } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { BreadcrumbDefinition, BreadcrumbService } from 'xng-breadcrumb';
import { EffectFactory } from '../../modules/shared/store/common/effect.factory';
import { AuthorizationService } from '../../services/authorization.service';
import { NavigatorGqlService } from '../../services/navigator.gql-service';
import { OrganizationService } from '../../services/organization.service';
import * as actions from './global.actions';
import { NotificationService } from '../../services/notification.service';
import { eventReceived } from './event';
import { pageWithoutLayout } from '../../app.utils';
import { SettingsService } from '../../services/settings.service';
import { parseJwt } from '@libs/shared/utils/parseJwt';
import { OrganizationGlobalStore } from '../organization.global-store';
import { isOffline } from '../../utils/connection.service';
import { LoggedUserGlobalStore } from '../logged-user.global-store';

@Injectable()
export class GlobalEffects {
    private actions$ = inject(Actions);
    private translate = inject(TranslateService);
    private router = inject(Router);
    private route = inject(ActivatedRoute);
    private authorizationService = inject(AuthorizationService);
    private navigatorService = inject(NavigatorGqlService);
    private notificationService = inject(NotificationService);
    private settingsService = inject(SettingsService);
    private organizationService = inject(OrganizationService);
    private organizationStore = inject(OrganizationGlobalStore);
    private store = inject(Store);
    private loggedUserStore = inject(LoggedUserGlobalStore);
    private breadcrumb = inject(BreadcrumbService);

    private factory = new EffectFactory(
        this.actions$,
        actions,
        this.authorizationService,
    ).create();
    private navigatorFactory = new EffectFactory(
        this.actions$,
        actions,
        this.navigatorService,
    ).create();

    changeLanguage$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(actions.setLanguage),
                map(({ language }) => this.translate.use(language)),
            ),
        { dispatch: false },
    );

    /**
     @Deprecated("used only for outdated store.select(currentOrganizationId)")
     */
    setSelectedOrganization$ = createEffect(() =>
        this.actions$.pipe(
            ofType(actions.setCurrentOrganizationId),
            tap(async ({ organizationId }) => {
                this.organizationStore.selectedId.set(organizationId || 0);
                if (organizationId) {
                    this.settingsService.setSelectedOrganizationId({
                        organizationId,
                    });
                }
            }),
            map(({ organizationId }) =>
                actions.globalSuccess({
                    input: { key: 'organizationChange', data: organizationId },
                }),
            ),
        ),
    );

    routeChange$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ROUTER_NAVIGATED),
            withLatestFrom(this.breadcrumb.breadcrumbs$),
            switchMap(([{ payload }, breadcrumbs]) => {
                if (isOffline()) return of(actions.ignore());
                if (window.history.state.fromHistory) {
                    return of(actions.ignore());
                }
                const url = (payload as any)?.event?.urlAfterRedirects;
                if (pageWithoutLayout(url) || url == '/') {
                    return of(actions.ignore());
                }

                this.navigatorService
                    .createVisitHistory({
                        url,
                        data: this.parseBreadcrumbsParams(breadcrumbs),
                    })
                ;

                return of(actions.ignore());
            }),
        ),
    );

    updateVisitHistory$ = createEffect(() =>
        this.actions$.pipe(
            ofType(actions.updateVisitHistoryData),
            withLatestFrom(this.breadcrumb.breadcrumbs$),
            switchMap(([action, breadcrumbs]) => {
                if (window.history.state.fromHistory)
                    return of(actions.ignore());
                if (isOffline()) return of(actions.ignore());

                this.navigatorService
                    .createVisitHistory({
                        url: this.router.url,
                        data: this.parseBreadcrumbsParams(breadcrumbs),
                    })
                ;

                return of(actions.ignore());
            }),
        ),
    );

    watchEvents$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(actions.watchEvents),
                switchMap(() =>
                    this.notificationService.eventSubscription().pipe(
                        map((event) => {
                            eventReceived.next(event.name);
                            return actions.ignore();
                        }),
                        catchError(() => of(actions.ignore())),
                    ),
                ),
            ),
        {},
    );

    postMessage$ = createEffect(() =>
        fromEvent(window, 'message').pipe(
            exhaustMap((event: Event) => {
                const token = (event as any as { data: { token: string } }).data.token;
                if (!token) return of(actions.ignore());
                return this.authorizationService.tokenLogin({ token }).pipe(
                    switchMap(() => {
                        const parsedToken = parseJwt(token);
                        const agendaId = Number(parsedToken.id_organization);
                        if (!agendaId || Number.isNaN(agendaId)) {
                            return of(actions.ignore());
                        }
                        return this.organizationService
                            .getOrganizationIdByAgendaId({ agendaId: agendaId.toString() })
                            .pipe(
                                map((organization) =>
                                    organization?.id
                                        ? actions.setCurrentOrganizationId({
                                            organizationId: organization.id,
                                            token,
                                        })
                                        : actions.ignore(),
                                ),
                                catchError(() => of(actions.ignore())),
                            );
                    }),
                    catchError(() => of(actions.ignore())),
                );
            }),
        ),
    );

    private parseBreadcrumbsParams(breadcrumbs: BreadcrumbDefinition[]) {
        const params: string[] = [];
        breadcrumbs.forEach((it, index) => {
            const parts = (it?.label?.toString() || '').split('|');
            if (parts.length > 1) {
                params.push(parts[1]);
            } else {
                params.push(`__${parts[0]}`);
            }
        });

        return JSON.stringify(params);
    }

    constructor() {
        window.parent.postMessage('TOKEN', '*');
    }
}
